import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolBox,
  ProtocolSite,
  ProtocolWarning,
} from "../types";

/**
 * Mounjaro KwikPen — tirzepatide, the weekly pen with four fixed doses, a
 * needle she attaches herself and a prime before every injection.
 *
 * ### One label, two languages, three devices
 *
 * The EMA product information covers a single-dose pen, a vial to draw up
 * and the KwikPen, each with its own instructions. Three walks live here:
 * the **KwikPen**, the presentation on the UK and French markets, the
 * **single-dose pen** and the **vial** below it. The device question tells a pen from a vial
 * but not a pen from a pen, so each names its `box` and the coach
 * asks which box she got. The English and French KwikPen instructions agree
 * step for step:
 * wash; cap straight off, pen and label inspected, colourless to slightly
 * yellow; the red inner seal wiped; a new needle, straight on and tight,
 * outer shield kept, inner shield binned; **prime** — two clicks to the long
 * line, needle up, cartridge tapped, knob pushed in and held for a count of
 * five until medicine appears, up to two more tries, then a new needle;
 * abdomen at least 5 cm from the navel or the thigh, a different site each
 * week; the knob turned to the full-dose icon; needle in, knob pushed and
 * held for a count of five, the icon confirmed; blood pressed and not
 * rubbed; outer shield back on, needle unscrewed and binned, cap on. The
 * English copy is the English instructions' and the French the French.
 *
 * ### What is left out
 *
 * The back of the upper arm, which the label gives to another person's
 * hands and the site question does not know. And the titration: the
 * prescriber's. The vial has its own walk below.
 *
 * ### Not a fertility drug
 *
 * As Ozempic: `metabolic`, stopped before a pregnancy is attempted, never a
 * trigger. Dose in milligrams, only the six strengths a pen comes in.
 *
 * Restated from `content/data/medications/glp1.json` (the tirzepatide
 * entry), whose pen procedure was written from these two instructions; the
 * entry's own contraception and preconception text is untouched.
 */

const MOUNJARO_BRANDS = [
  { name: "Mounjaro", markets: ["EU", "GB"] },
] as const satisfies readonly ProtocolBrand[];

/** The leaflet's four-day rule and three-day minimum, both languages. */
const MISSED_DOSE: ProtocolWarning = {
  id: "mounjaro.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Missed dose - If it has been 4 days or less since you should have used it, use it as soon as you remember, then inject your next dose as usual on your scheduled day. If it has been more than 4 days, skip the missed dose and inject your next dose as usual on your scheduled day. Do not use a double dose to make up for a forgotten dose; the minimum time between two doses is 3 days.",
    fr: "Dose oubliée - Si 4 jours ou moins se sont écoulés depuis la date de l'injection oubliée, procédez à l'injection dès que vous vous en rendez compte, puis injectez la dose suivante comme d'habitude le jour prévu. Si plus de 4 jours se sont écoulés, n'injectez pas la dose oubliée et injectez la dose suivante comme d'habitude le jour prévu. Ne prenez pas de dose double pour compenser la dose que vous avez oublié de prendre ; le délai minimum entre deux doses est de 3 jours.",
  },
  sourceSegment:
    "⚠ Missed dose - If it has been 4 days or less since you should have used it, use it as soon as you remember, then inject your next dose as usual on your scheduled day. If it has been more than 4 days, skip the missed dose and inject your next dose as usual on your scheduled day. Do not use a double dose to make up for a forgotten dose; the minimum time between two doses is 3 days.",
};

const ALLERGIC_REACTION: ProtocolWarning = {
  id: "mounjaro.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Breathing problems, rapid swelling of the lips, tongue or throat with difficulty swallowing, or a fast heartbeat are signs of a severe allergic reaction: get emergency help straight away.",
    fr: "Aide urgente - Des problèmes respiratoires, un gonflement rapide des lèvres, de la langue ou de la gorge avec des difficultés à avaler, ou un rythme cardiaque rapide sont les signes d'une réaction allergique sévère : appelez les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Breathing problems, rapid swelling of the lips, tongue or throat with difficulty swallowing, or a fast heartbeat are signs of a severe allergic reaction: get emergency help straight away.",
};

const PANCREATITIS: ProtocolWarning = {
  id: "mounjaro.pancreatitis",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Stomach and back pain - Severe pain in the stomach and back which does not go away can be an inflamed pancreas: stop and see a doctor immediately.",
    fr: "Douleur à l'estomac et au dos - Une douleur sévère qui persiste dans l'estomac et le dos peut être une inflammation du pancréas : arrêtez et consultez immédiatement un médecin.",
  },
  sourceSegment:
    "⚠ Stomach and back pain - Severe pain in the stomach and back which does not go away can be an inflamed pancreas: stop and see a doctor immediately.",
};

/** Thigh or abdomen for her own hands; the arm is another person's. */
const SITES: readonly ProtocolSite[] = [
  {
    id: "abdomen",
    label: { en: "Abdomen", fr: "Ventre" },
    copy: {
      en: "Choose the site - Inject here, on your abdomen, at least 5 cm from the belly button. Change your injection site each week: you may use the same area of your body, but choose a different site within that area.",
      fr: "Choisir le site - Injectez ici, sur le ventre, à au moins 5 cm du nombril. Changez de site d'injection chaque semaine : vous pouvez utiliser la même zone du corps, mais choisissez un site d'injection différent dans cette zone.",
    },
  },
  {
    id: "thigh",
    label: { en: "Thigh", fr: "Cuisse" },
    copy: {
      en: "Choose the site - Inject here, on your thigh. Change your injection site each week: you may use the same area of your body, but choose a different site within that area.",
      fr: "Choisir le site - Injectez ici, sur la cuisse. Changez de site d'injection chaque semaine : vous pouvez utiliser la même zone du corps, mais choisissez un site d'injection différent dans cette zone.",
    },
  },
];

/**
 * The two boxes "a pen" can mean. The labels are what the box says: KwikPen
 * on the one, and on the other a pen with a hidden needle, which is the thing
 * she notices first.
 */
const KWIKPEN: ProtocolBox = {
  id: "kwikpen",
  label: { en: "KwikPen (4 doses)", fr: "KwikPen (4 doses)" },
};
const SINGLE_DOSE_PEN: ProtocolBox = {
  id: "single-dose-pen",
  label: {
    en: "Single-dose pen (hidden needle)",
    fr: "Stylo à dose unique (aiguille cachée)",
  },
};

/** The frequently-asked question the single-dose instructions end on. */
const UNLOCKED_WITH_THE_CAP_ON: ProtocolWarning = {
  id: "mounjaro.single-dose-pen.unlocked-with-the-cap-on",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Pressed too early - If you unlocked the pen and pressed the purple injection button before pulling off the grey base cap, do not remove the grey base cap: throw away the pen and get a new pen.",
    fr: "Appuyé trop tôt - Si vous avez déverrouillé le stylo et appuyé sur le bouton d'injection violet avant de retirer le capuchon gris de la base, ne retirez pas le capuchon gris de la base : jetez le stylo et prenez un nouveau stylo.",
  },
  sourceSegment:
    "⚠ Pressed too early - If you unlocked the pen and pressed the purple injection button before pulling off the grey base cap, do not remove the grey base cap: throw away the pen and get a new pen.",
};

export const MOUNJARO_PROTOCOL: InjectionProtocol = {
  medicationSlug: "mounjaro",
  brands: MOUNJARO_BRANDS,
  // Zepbound is the US name and its label was not read.
  aliases: ["Tirzepatide"],
  indication: "metabolic",
  dose: { unit: "mg", strengths: [2.5, 5, 7.5, 10, 12.5, 15] },
  form: "pen",
  prescriptionForms: ["pre_filled_pen"],
  route: "subcutaneous",
  version: "2026-10-06.1",
  derivedFrom: "content/data/medications/glp1.json#tirzepatide",
  box: KWIKPEN,
  sites: SITES,
  steps: [
    {
      id: "mounjaro.pen.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Wash your hands with soap and water. Pull the pen cap straight off and inspect the pen and its label: do not use it if the medicine name or dose strength does not match your prescription{dose}, if it is expired or looks damaged, or if the medicine has been frozen, has particles, is cloudy or discoloured; it should be colourless to slightly yellow. Lay out a new pen needle, a swab, gauze or a cotton ball, and a sharps container. {brand} is injected once a week, on the same day each week if you can, at any time of the day, with or without meals.",
        fr: "Préparer - Lavez-vous les mains avec du savon et de l'eau. Retirez le capuchon du stylo en tirant de façon bien droite et inspectez le stylo et l'étiquette : ne l'utilisez pas si le nom du médicament ou le dosage ne correspond pas à votre ordonnance{dose}, si la date de péremption est dépassée ou si le stylo semble endommagé, ou si le médicament a été congelé, contient des particules, est trouble ou présente une coloration anormale ; il doit être incolore à légèrement jaune. Disposez une nouvelle aiguille pour stylo, du coton ou une compresse de gaze et un collecteur. {brand} s'injecte une fois par semaine, le même jour chaque semaine si possible, à tout moment de la journée, pendant ou en dehors des repas.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      id: "mounjaro.pen.attach-the-needle",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Attach the needle - Wipe the red inner seal with a swab. Take a new pen needle, always a new one for each injection to help prevent infections and blocked needles, and pull the paper tab off the outer needle shield. Push the capped needle straight onto the pen and twist it on until it is tight. Pull off the outer needle shield and keep it, you will reuse it; pull off the inner needle shield and throw it away.",
        fr: "Fixer l'aiguille - Nettoyez la membrane en caoutchouc rouge avec du coton. Prenez une nouvelle aiguille pour stylo, toujours une nouvelle aiguille à chaque injection afin de prévenir les infections et l'obstruction des aiguilles, et retirez la languette en papier du capuchon externe de l'aiguille. Vissez de façon bien droite l'aiguille munie de ses capuchons sur le stylo jusqu'à ce qu'elle soit bien fixée. Retirez le capuchon externe de l'aiguille et conservez-le, il sera réutilisé ; retirez le capuchon interne de l'aiguille et jetez-le.",
      },
    },
    {
      // Every injection, not only a new pen — the one difference from the
      // Ozempic flow check that matters at the fridge.
      id: "mounjaro.pen.check-for-a-droplet",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Prime the pen - Slowly turn the dose knob until you hear 2 clicks and the long line shows in the dose window: this is the prime position, and it can be corrected by turning the knob either way until it lines up with the dose indicator. Hold the pen with the needle pointing up and tap the cartridge holder gently to collect the air bubbles at the top. Release some medicine into the air by pushing the dose knob in until it stops, then slowly count to 5 while holding it; do not inject into your body. Your pen is primed if a small amount of medicine comes out of the needle tip. If you see none, repeat the priming no more than 2 more times; if there is still none, change the pen needle and prime once more; if there is still none, do not use the pen and contact the manufacturer listed in your leaflet or your pharmacist.",
        fr: "Purger le stylo - Tournez lentement le bouton d'injection jusqu'à ce que vous entendiez 2 clics et que le trait long apparaisse dans la fenêtre de lecture : c'est la position de purge, et elle peut être corrigée en tournant le bouton dans un sens ou dans l'autre jusqu'à ce qu'elle s'aligne avec l'indicateur de dose. Tenez le stylo avec l'aiguille pointée vers le haut et tapotez doucement le porte-cartouche pour faire remonter les bulles d'air à la surface. Libérez un peu de médicament dans le vide en poussant le bouton d'injection jusqu'à la butée, puis comptez lentement jusqu'à 5 tout en le maintenant enfoncé ; n'injectez pas dans votre corps. Votre stylo est purgé si une petite quantité de médicament sort de la pointe de l'aiguille. Si vous n'en voyez pas, répétez la purge pas plus de 2 fois supplémentaires ; s'il n'y en a toujours pas, changez l'aiguille du stylo et purgez encore une fois ; s'il n'y en a toujours pas, n'utilisez pas le stylo et contactez le fabricant indiqué dans votre notice ou votre pharmacien.",
      },
    },
    {
      id: "mounjaro.pen.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: SITES[0]!.copy,
    },
    {
      id: "mounjaro.pen.inspect-dose-window",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Select your dose - Turn the dose knob until it stops and the full-dose icon shows in the dose window: the icon is one full dose{dose}, the one dose this pen gives each week. The pen will not let you dial a full dose after its 4 weekly doses; if you cannot turn the knob to the icon, dispose of the pen and use a new one, and never inject the leftover medicine.",
        fr: "Sélectionner votre dose - Tournez le bouton d'injection jusqu'au bout afin que l'icône de dose complète apparaisse dans la fenêtre de lecture : l'icône équivaut à une dose complète{dose}, la seule dose que ce stylo délivre chaque semaine. Le stylo ne vous laissera pas sélectionner une dose complète après ses 4 doses hebdomadaires ; si vous ne pouvez pas tourner le bouton jusqu'à l'icône, jetez le stylo et utilisez-en un nouveau, et n'injectez jamais le médicament restant.",
      },
    },
    {
      // No angle in the label — "insert the needle into your skin" — so none
      // is said and the figure draws none.
      id: "mounjaro.pen.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Insert the needle into your skin. Push the dose knob in until it stops, then slowly count to 5 while holding it; the icon must show in the dose window before you remove the needle. Pull the needle out of your skin; a drop of medicine on the needle tip is normal and does not affect your dose. Confirm the icon is in the dose window: if it is, you have received the full dose; if it is not, insert the needle back into your skin and finish the injection without redialling the dose. If you still think you did not receive the full dose, do not start over and do not repeat the injection.",
        fr: "Injecter - Insérez l'aiguille dans votre peau. Appuyez sur le bouton d'injection jusqu'à la butée, puis comptez lentement jusqu'à 5 tout en le maintenant enfoncé ; l'icône doit apparaître dans la fenêtre de lecture avant de retirer l'aiguille. Retirez l'aiguille de votre peau ; la présence d'une goutte de médicament sur la pointe de l'aiguille est normale et n'affectera pas votre dose. Vérifiez que l'icône se trouve dans la fenêtre de lecture : si c'est le cas, vous avez reçu la dose complète ; sinon, réinsérez l'aiguille dans votre peau et terminez votre injection sans resélectionner la dose. Si vous pensez toujours ne pas avoir reçu la dose complète, ne recommencez pas et ne répétez pas l'injection.",
      },
      warnings: [ALLERGIC_REACTION],
    },
    {
      id: "mounjaro.pen.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - If you see blood, lightly press the site with gauze or a cotton ball; do not rub it. Carefully replace the outer needle shield, unscrew the capped needle and put it in your sharps container; never store the pen with the needle attached, and never put loose needles in household waste. Replace the pen cap. A pen in use keeps in the refrigerator or below 30 degrees Celsius for 30 days, and is disposed of after its 4 weekly doses even if medicine is left in it.",
        fr: "Après l'injection - Si vous voyez du sang, appuyez légèrement sur le site avec une compresse de gaze ou une boule de coton ; ne frottez pas. Remettez soigneusement en place le capuchon externe de l'aiguille, dévissez l'aiguille munie de son capuchon et jetez-la dans votre collecteur ; ne conservez jamais le stylo avec l'aiguille fixée dessus, et ne jetez jamais d'aiguilles en vrac dans les ordures ménagères. Remettez en place le capuchon du stylo. Un stylo en cours d'utilisation se conserve au réfrigérateur ou à une température inférieure à 30 degrés Celsius pendant 30 jours, et se jette après ses 4 doses hebdomadaires même s'il reste du médicament.",
      },
      warnings: [PANCREATITIS],
    },
  ],
};

/**
 * Mounjaro single-dose pen — the same tirzepatide, one dose, a hidden needle
 * that goes in when she presses and comes back out on its own.
 *
 * ### The same label, the other pen
 *
 * The single-dose pen's instructions, English and French, in the same EMA
 * document as the KwikPen's: out of the fridge, grey base cap left on, label
 * and liquid inspected, hands washed; abdomen at least 5 cm from the navel
 * or the thigh, a different site each week; the pen checked locked, the cap
 * pulled straight off and binned, the needle never touched; the clear base
 * flat on the skin, the lock ring turned, the purple button pressed and held
 * up to ten seconds through two clicks until the grey plunger shows; the
 * whole pen to the sharps container. There is no needle to attach, nothing
 * to prime and no dose to dial, which is why it is its own walk and not a
 * branch of the KwikPen's. Both pens carry a `box` so the coach can
 * ask which box she got.
 *
 * ### What is left out
 *
 * As the KwikPen: the back of the upper arm, the vial, the titration, and
 * the manufacturer's phone line when the plunger does not show.
 */
export const MOUNJARO_SINGLE_DOSE_PEN_PROTOCOL: InjectionProtocol = {
  medicationSlug: "mounjaro",
  brands: MOUNJARO_BRANDS,
  aliases: ["Tirzepatide"],
  indication: "metabolic",
  dose: { unit: "mg", strengths: [2.5, 5, 7.5, 10, 12.5, 15] },
  form: "pen",
  prescriptionForms: ["pre_filled_pen"],
  route: "subcutaneous",
  version: "2026-10-06.1",
  derivedFrom: "content/data/medications/glp1.json#tirzepatide",
  box: SINGLE_DOSE_PEN,
  sites: SITES,
  steps: [
    {
      id: "mounjaro.single-dose-pen.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Take the pen out of the refrigerator and leave the grey base cap on until you are ready to inject; it is not necessary to warm the pen to room temperature. Check the pen label to make sure you have the right medicine and dose{dose}, and that it has not expired. Inspect the pen to make sure that it is not damaged; the pen has glass parts, so if you drop it on a hard surface do not use it and take a new pen. Make sure the medicine is not frozen, not cloudy, colourless to slightly yellow, and does not have particles; air bubbles are normal. Wash your hands. {brand} is used 1 time each week.",
        fr: "Préparer - Sortez le stylo du réfrigérateur et laissez le capuchon gris de la base jusqu'à ce que vous soyez prêt pour l'injection ; il n'est pas nécessaire de réchauffer le stylo à température ambiante. Vérifiez l'étiquette du stylo pour vous assurer que vous avez le bon médicament et le bon dosage{dose}, et que la date de péremption n'est pas dépassée. Inspectez le stylo pour vous assurer qu'il n'est pas endommagé ; certaines parties du stylo sont en verre, donc si vous le faites tomber sur une surface dure, ne l'utilisez pas et prenez un nouveau stylo. Assurez-vous que le médicament n'est pas congelé, n'est pas trouble, est incolore à légèrement jaune et ne contient pas de particules ; la présence de bulles d'air est normale. Lavez-vous les mains. {brand} est utilisé 1 fois par semaine.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      id: "mounjaro.single-dose-pen.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: SITES[0]!.copy,
    },
    {
      // The needle is hidden: the cap comes off, nothing goes on.
      id: "mounjaro.single-dose-pen.ready-the-pen",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Pull off the grey base cap - Make sure the pen is locked. Do not unlock the pen until you place the clear base on your skin and are ready to inject. Pull the grey base cap straight off and throw it away. Do not put the grey base cap back on, this could damage the needle. Do not touch the needle; a drop of liquid on the tip of the needle is normal.",
        fr: "Retirer le capuchon gris de la base - Vérifiez que le stylo est verrouillé. Ne déverrouillez pas le stylo tant que vous n'avez pas placé la base transparente à plat contre la peau et que vous n'êtes pas prêt à injecter. Retirez le capuchon gris de la base en tirant de façon bien droite et jetez-le. Ne remettez pas le capuchon gris de la base en place car cela risquerait d'endommager l'aiguille. Ne touchez pas l'aiguille ; il est normal qu'il y ait une goutte de liquide au bout de l'aiguille.",
      },
      warnings: [UNLOCKED_WITH_THE_CAP_ON],
    },
    {
      // No angle in the label: the clear base sits flat and the pen does the
      // rest, so the figure draws a straight pen.
      id: "mounjaro.single-dose-pen.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Place the clear base flat against your skin at the injection site. Unlock by turning the lock ring. Press and hold the purple injection button for up to 10 seconds. Listen for two clicks: the first click means the injection has started, the second click means the injection is completed. Some people hear a soft click right before the second loud click; that is normal, do not remove the pen from your skin until you hear the second loud click. You will know your injection is complete when the grey plunger is visible.",
        fr: "Injecter - Placez la base transparente à plat contre la peau au niveau du site d'injection. Déverrouillez en tournant la bague de sécurité. Appuyez et maintenez enfoncé le bouton d'injection violet pendant 10 secondes au maximum. Vous devez entendre deux clics : le premier clic signifie que l'injection a commencé, le deuxième clic signifie que l'injection est terminée. Certaines personnes entendent un clic léger juste avant le deuxième clic fort ; c'est normal, ne retirez pas le stylo de votre peau tant que vous n'avez pas entendu le deuxième clic fort. Vous saurez que votre injection est terminée lorsque le piston gris est visible.",
      },
      warnings: [ALLERGIC_REACTION],
    },
    {
      id: "mounjaro.single-dose-pen.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Check that the grey plunger is visible: if it is, your dose was delivered the right way. If you do not see the grey plunger, contact the manufacturer listed in your leaflet for further instructions, and until then store your pen safely to avoid an accidental needle injury. A drop of liquid or blood on your skin is normal: press a cotton ball or gauze over the injection site and do not rub it. Place the used pen in a sharps container; do not throw away pens in your household waste and do not recycle your used sharps container. Unused pens keep in the refrigerator, or below 30 degrees Celsius for up to 21 days.",
        fr: "Après l'injection - Vérifiez que le piston gris est visible : si c'est le cas, votre dose a été administrée correctement. Si vous ne voyez pas le piston gris, contactez le fabricant indiqué dans votre notice pour obtenir des instructions, et en attendant conservez votre stylo en lieu sûr pour éviter toute blessure accidentelle avec l'aiguille. Il est normal qu'il y ait une goutte de liquide ou de sang sur la peau : appuyez une boule de coton ou une compresse de gaze sur le site d'injection et ne frottez pas. Placez le stylo utilisé dans un collecteur pour objets piquants ; ne jetez pas les stylos avec vos ordures ménagères et ne recyclez pas votre collecteur usagé. Les stylos non utilisés se conservent au réfrigérateur, ou à une température inférieure à 30 degrés Celsius pendant 21 jours au maximum.",
      },
      warnings: [PANCREATITIS],
    },
  ],
};

/**
 * Mounjaro vial — the same tirzepatide drawn up from a single-dose vial with
 * a syringe she supplies.
 *
 * ### The same label, the third device
 *
 * The vial leaflet's twelve numbered instructions, English and French, in
 * the same EMA document: hands washed; the solution clear and colourless to
 * slightly yellow, not frozen, cloudy or with particles; the plastic cap off
 * and the stopper left on and swabbed; a new syringe, never shared or
 * reused; a little air drawn and pushed into the vial; vial and syringe
 * upside down, all the solution drawn (the vial holds one dose); bubbles
 * tapped up and pushed out; the skin cleaned, a fold pinched; injected under
 * the skin as taught, all of it, the needle left in for five seconds; vial,
 * needle and syringe to a puncture-resistant container at once. No angle is
 * stated. The vial is its own device, so the device question tells it from
 * the pens and it carries no `box`.
 *
 * ### What is left out
 *
 * As the pens: the back of the upper arm, the titration, and the insulin
 * co-injection note (a different site for the insulin), which is the
 * prescriber's.
 */
export const MOUNJARO_VIAL_PROTOCOL: InjectionProtocol = {
  medicationSlug: "mounjaro",
  brands: MOUNJARO_BRANDS,
  aliases: ["Tirzepatide"],
  indication: "metabolic",
  dose: { unit: "mg", strengths: [2.5, 5, 7.5, 10, 12.5, 15] },
  form: "vial",
  // A ready solution she draws up with her own syringe, like Lupron.
  prescriptionForms: ["injection_solution"],
  route: "subcutaneous",
  version: "2026-10-06.1",
  derivedFrom: "content/data/medications/glp1.json#tirzepatide",
  sites: SITES,
  steps: [
    {
      id: "mounjaro.vial.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - First wash your hands with soap and water. Check the expiry date on the vial label and that it is the dose your doctor prescribed{dose}. Check that the medicine in the vial looks clear and colourless to slightly yellow; do not use it if it is frozen, cloudy or has particles in it, or if the vial, seal or stopper is damaged. Pull off the vial plastic protective cap, but do not remove the stopper. Clean the stopper on the vial with a swab and prepare a new syringe; do not share or reuse your needle or syringe. Lay out a sharps container. {brand} is used 1 time each week.",
        fr: "Préparer - Lavez-vous d'abord les mains à l'eau et au savon. Vérifiez la date de péremption sur l'étiquette du flacon et qu'il s'agit de la dose prescrite par votre médecin{dose}. Vérifiez que le médicament contenu dans le flacon est clair et incolore à légèrement jaune ; ne l'utilisez pas s'il est gelé, trouble ou contient des particules, ou si le flacon, le sceau ou le bouchon est endommagé. Retirez le capuchon de protection en plastique du flacon, mais ne retirez pas le bouchon. Nettoyez le bouchon du flacon avec un coton et préparez une nouvelle seringue ; ne partagez pas et ne réutilisez pas votre aiguille ou votre seringue. Disposez un collecteur. {brand} est utilisé 1 fois par semaine.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      id: "mounjaro.vial.draw-up-the-dose",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Draw up the dose - Draw a small amount of air into the syringe. Put the needle through the rubber stopper on top of the vial and inject the air into the vial. Turn the vial and the syringe upside down and slowly pull the syringe plunger down to withdraw all the solution from the vial; the vial is filled to enable delivery of a single 0.5 ml dose. If there are air bubbles in the syringe, tap the syringe gently a few times to let any air bubbles rise to the top, then slowly push the plunger up until there is no more air in the syringe. Pull the syringe out of the vial stopper.",
        fr: "Prélever la dose - Aspirez une petite quantité d'air dans la seringue. Placez l'aiguille à travers le bouchon en caoutchouc sur le dessus du flacon et injectez l'air dans le flacon. Retournez le flacon et la seringue et tirez lentement le piston de la seringue vers le bas pour prélever toute la solution du flacon ; le flacon est rempli pour permettre l'administration d'une dose unique de 0,5 ml. S'il y a des bulles d'air dans la seringue, tapotez légèrement la seringue plusieurs fois pour laisser remonter les bulles d'air vers le haut, puis poussez lentement le piston vers le haut jusqu'à ce qu'il n'y ait plus d'air dans la seringue. Retirez la seringue du bouchon du flacon.",
      },
    },
    {
      id: "mounjaro.vial.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: SITES[0]!.copy,
    },
    {
      // "As you have been instructed": no angle, so none is drawn.
      id: "mounjaro.vial.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Clean your skin. Gently pinch and hold a fold of skin where you will inject. Inject under the skin, as you have been instructed; do not inject directly into a vein. Inject all the solution from the syringe to receive a full dose. After your injection, the needle should stay under your skin for 5 seconds to make sure you receive the full dose. Then pull the needle out of your skin.",
        fr: "Injecter - Nettoyez votre peau. Pincez doucement et maintenez un pli de peau à l'endroit où vous allez injecter. Injectez sous la peau, comme on vous l'a appris ; n'injectez pas directement dans une veine. Injectez toute la solution de la seringue pour recevoir une dose complète. Après votre injection, l'aiguille doit rester sous votre peau pendant 5 secondes pour s'assurer que vous recevez la dose complète. Retirez ensuite l'aiguille de votre peau.",
      },
      warnings: [ALLERGIC_REACTION],
    },
    {
      id: "mounjaro.vial.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Throw away the vial, used needle and syringe immediately after each injection in a puncture-resistant sharps container, or as instructed by your doctor, nurse or pharmacist. Unused vials keep in the refrigerator in the original packaging to protect them from light, never frozen; a vial can be stored unrefrigerated below 30 degrees Celsius for up to 21 cumulative days, and then it must be discarded.",
        fr: "Après l'injection - Jetez le flacon, l'aiguille et la seringue usagées immédiatement après chaque injection dans un collecteur résistant à la perforation, ou selon les instructions de votre médecin, infirmier/ère ou pharmacien. Les flacons non utilisés se conservent au réfrigérateur dans leur emballage d'origine à l'abri de la lumière, jamais congelés ; un flacon peut être conservé en dehors du réfrigérateur à une température inférieure à 30 degrés Celsius pendant une durée maximale cumulée de 21 jours, et ensuite il doit être jeté.",
      },
      warnings: [PANCREATITIS],
    },
  ],
};

export const MOUNJARO_PROTOCOLS = [
  MOUNJARO_PROTOCOL,
  MOUNJARO_SINGLE_DOSE_PEN_PROTOCOL,
  MOUNJARO_VIAL_PROTOCOL,
] as const satisfies readonly InjectionProtocol[];
